import { format, isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { noop } from '../../../utils/noop.js';
import { Button } from '../../Button/index.js';
import { Calendar } from '../Calendar/index.js';
import { DateInput } from '../date_input/date_input.js';
import { useActiveDate, useDateRangeState } from '../hooks/index.js';
import { DatePickerLayout } from '../layout/date_picker_layout.js';
import { MAX_DATE, MIN_DATE } from '../utilities/constants.js';
import { applyMessage, cancelMessage, getInitialActiveDate, getTodayDate, validateSelectedDateRange } from '../utilities/index.js';
import { useDateFormat } from '../hooks/useDateFormat.js';
import { useMinMaxDate } from '../hooks/useMinMaxDate.js';
/**
 * Allows the user to navigate a calendar and select a date range.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { DateRangePicker } from "@panwds/react-ui";
 * ```
 */
export const DateRangePicker = ({ dataMetrics, dataTestId, initialActiveDate = startOfDay(getTodayDate()), dateFormat = 'Pp', initialDateRange, max: propsMax = MAX_DATE, min: propsMin = MIN_DATE, onApply, onCancel = noop, weekStartsOn = 0, disabled = false, placeHolder, placeholder, label }) => {
    const intl = useIntl();
    const { pattern } = useDateFormat(dateFormat);
    const { min, max } = useMinMaxDate({ min: propsMin, max: propsMax, pattern });
    const [activeDate, setActiveDate] = useActiveDate({
        initialActiveDate: getInitialActiveDate({ initialActiveDate, initialDate: initialDateRange?.start }),
        max,
        min
    });
    const { selectDate, setEndDate, setStartDate, dateRange } = useDateRangeState(initialDateRange, min, max);
    const [hoverDate, setHoverDate] = useState();
    useEffect(() => {
        if (!initialDateRange || (!initialDateRange.start && !initialDateRange.end)) {
            setStartDate(undefined);
            setEndDate(undefined);
            return;
        }
        if (validateSelectedDateRange({
            start: initialDateRange.start,
            end: initialDateRange.end,
            min,
            max
        }) &&
            (dateRange?.end && dateRange?.start
                ? format(dateRange.end, pattern) !== format(dateRange.end, pattern) ||
                    format(initialDateRange.start, pattern) !== format(dateRange.start, pattern)
                : true)) {
            setStartDate(initialDateRange.start);
            setEndDate(initialDateRange.end);
            setActiveDate(startOfDay(initialDateRange.start));
        }
    }, [initialDateRange]);
    function handleSelectDate(next) {
        selectDate(next);
        setActiveDate(next);
        setHoverDate(undefined);
    }
    function handleStart(date) {
        if (!dateRange.end) {
            setStartDate(date);
        }
        else if (isAfter(date, dateRange.end) || isEqual(date, dateRange.end)) {
            setStartDate(date);
            setEndDate(date);
        }
        else {
            setStartDate(date);
        }
    }
    function handleEnd(date) {
        if (!dateRange.start) {
            setEndDate(date);
        }
        else if (isBefore(date, dateRange.start) || isEqual(date, dateRange.start)) {
            setStartDate(date);
            setEndDate(date);
        }
        else {
            setEndDate(date);
        }
    }
    return (React.createElement(DatePickerLayout, { dataMetrics: dataMetrics, dataTestId: dataTestId || 'date-range-picker', actions: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: () => {
                    onCancel();
                }, disabled: disabled }, intl.formatMessage(cancelMessage)),
            React.createElement(Button, { appearance: "primary", disabled: !dateRange.end || !dateRange.start || disabled, onClick: () => {
                    onApply(dateRange);
                } }, intl.formatMessage(applyMessage))), calendar: React.createElement(Calendar, { activeDate: activeDate, hoverDate: hoverDate, max: max, min: min, onActiveDateChange: setActiveDate, onHoverDateChange: setHoverDate, onSelectedDateChange: handleSelectDate, dateRange: dateRange, weekStartsOn: weekStartsOn }), inputs: React.createElement("div", { className: "twds-flex twds-space-x-1" },
            React.createElement(DateInput, { label: label?.dateInput?.start, showLabel: !!label?.dateInput?.start, className: { inputClassName: 'twds-box-border twds-w-[132px]' }, formatPattern: dateFormat, placeholder: (placeholder ?? placeHolder)?.dateInput?.start, max: max, min: min, selectedDate: dateRange.start, onSelectDate: handleStart, connectLabel: true }),
            React.createElement(DateInput, { label: label?.dateInput?.end, showLabel: !!label?.dateInput?.end, className: { inputClassName: 'twds-box-border twds-w-[132px]' }, formatPattern: dateFormat, placeholder: (placeholder ?? placeHolder)?.dateInput?.end, max: max, min: min, selectedDate: dateRange.end, onSelectDate: handleEnd, connectLabel: true })) }));
};
//# sourceMappingURL=date_range_picker.js.map